'use client';

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRight, RefreshCw, X } from 'lucide-react';
import SpinePlayer from './spine-player';
import {
  type BannerHeroSource,
  type BannerLive2dSlot,
} from '../lib/heroes/banner';
import { coverTheme } from '../lib/heroes/theme';
import type { SpineAsset } from '../lib/heroes/types';
import { defaultSpineRuntimeForGame, warmSpineRuntime } from '../lib/live2d/spine-engine';

export type { BannerLive2dSlot };

const MOBILE_MAX = 760;

/**
 * Desktop: right-side Live2D only. WAP: one centered Live2D.
 * Facing toward the search box is decided inside the spine player.
 */
function bannerAsset(
  portrait: SpineAsset,
  side: BannerLive2dSlot['side'],
  opts?: { mobile?: boolean; gameId?: string },
): SpineAsset {
  if (side === 'center') {
    // Keep framing stable across expand/collapse — remounting Spine causes visible shake.
    return {
      ...portrait,
      coverHeight: true,
      bannerSide: 'center',
      bannerInnerRatio: 0.5,
      bannerOuterRatio: 0.5,
      // Body fills the viewport; canvas is full-screen so props draw under chrome.
      scale: opts?.mobile ? 0.94 : 0.92,
      framing: {
        ...portrait.framing,
        offsetY: opts?.mobile
          ? (portrait.framing?.offsetY || 0) - 0.01
          : (portrait.framing?.offsetY ?? 0.01),
      },
    };
  }
  return {
    ...portrait,
    coverHeight: true,
    bannerSide: side,
    bannerInnerRatio: 0.64,
    bannerOuterRatio: 0.92,
    scale: opts?.gameId === 'guardian-tales' ? 1.28 : 0.9,
    framing: {
      offsetY: 0.01,
      ...portrait.framing,
      offsetX: (portrait.framing?.offsetX || 0) + 0.04,
    },
  };
}

function useIsNarrowBanner() {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return narrow;
}

function findBannerHero(heroes: BannerHeroSource[], id: string, gameId: string) {
  return (
    heroes.find((hero) => hero.id === id && hero.gameId === gameId) ||
    heroes.find((hero) => hero.id === id) ||
    null
  );
}

function slotFromHero(
  heroes: BannerHeroSource[],
  id: string,
  gameId: string,
  side: BannerLive2dSlot['side'],
): BannerLive2dSlot | null {
  const hero = findBannerHero(heroes, id, gameId);
  const form = hero?.forms[0];
  if (!hero || !form?.portrait) return null;
  return {
    id: hero.id,
    name: hero.name,
    side,
    portrait: form.portrait,
    fallback: form.fallback ?? form.head ?? hero.head,
  };
}

/** Same as detail page: pick a random quote, avoid the last shown index. */
function nextHeroQuote(gameId: string, heroId: string, quotes: string[]) {
  if (!quotes.length) return '';
  if (quotes.length === 1) return quotes[0];
  let previous = Number.NaN;
  try {
    previous = Number(sessionStorage.getItem(`${gameId}-${heroId}-quote-index`));
  } catch { /* ignore */ }
  const candidates = quotes
    .map((_, index) => index)
    .filter((index) => index !== previous);
  const next = candidates[Math.floor(Math.random() * candidates.length)] ?? 0;
  try {
    sessionStorage.setItem(`${gameId}-${heroId}-quote-index`, String(next));
  } catch { /* ignore */ }
  return quotes[next] ?? '';
}

export default function HeroBannerLive2d({
  slots,
  heroes = [],
  gameId = 'arcanatactics',
  playing = false,
  onThemeChange,
}: {
  slots: BannerLive2dSlot[];
  /** Catalog-backed heroes with Live2D portrait (same source as 英雄图鉴). */
  heroes?: BannerHeroSource[];
  gameId?: string;
  playing?: boolean;
  onThemeChange?: (theme: string | null) => void;
}) {
  const isMobile = useIsNarrowBanner();
  const initial = useMemo(() => {
    const right = slots.find((s) => s.side === 'right') || slots[slots.length - 1] || slots[0];
    return right || null;
  }, [slots]);

  const [activeId, setActiveId] = useState(initial?.id || '');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [portalReady, setPortalReady] = useState(false);
  const [heroQuote, setHeroQuote] = useState('');

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    if (initial?.id) setActiveId(initial.id);
  }, [initial?.id]);

  useEffect(() => {
    const hero = activeId ? findBannerHero(heroes, activeId, gameId) : null;
    onThemeChange?.(hero ? coverTheme(hero) : null);
  }, [activeId, gameId, heroes, onThemeChange]);

  useEffect(() => () => onThemeChange?.(null), [onThemeChange]);

  useEffect(() => {
    if (!playing || !activeId) return;
    const hero = findBannerHero(heroes, activeId, gameId);
    setHeroQuote(nextHeroQuote(gameId, activeId, hero?.quotes || []));
  }, [playing, activeId, gameId, heroes]);

  const displaySide: BannerLive2dSlot['side'] = isMobile ? 'center' : 'right';
  const active =
    (activeId && slotFromHero(heroes, activeId, gameId, displaySide)) ||
    (initial ? { ...initial, side: displaySide } : null);

  const candidates = useMemo(
    () =>
      heroes.filter(
        (hero) =>
          (!gameId || hero.gameId === gameId) &&
          hero.forms.some((form) => !!form.portrait),
      ),
    [gameId, heroes],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (hero) =>
        hero.name.toLowerCase().includes(q) ||
        hero.id.toLowerCase().includes(q) ||
        hero.latin.toLowerCase().includes(q),
    );
  }, [candidates, search]);

  useEffect(() => {
    if (!selectorOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectorOpen(false);
    };
    window.addEventListener('keydown', onKey);
    // 仅预热 runtime；Live2D 资源等选中角色后再由 SpinePlayer 加载
    warmSpineRuntime(defaultSpineRuntimeForGame(gameId));
    return () => window.removeEventListener('keydown', onKey);
  }, [selectorOpen, gameId]);

  if (!active) return null;

  const heroData = findBannerHero(heroes, active.id, gameId);
  const head = heroData?.head || active.fallback;
  const quote = heroQuote.trim();

  const live2dAsset = bannerAsset(active.portrait, displaySide, {
    mobile: isMobile,
    gameId,
  });

  return (
    <>
      <div
        className={`gk-hero-live2d${
          displaySide === 'right' ? ' is-right' : ' is-center'
        }${gameId === 'guardian-tales' ? ' is-guardian-tales' : ''}`}
        aria-hidden="true"
      >
        <div className="gk-hero-live2d-inner">
          {/* key 强制硬切：换角色立即卸旧建新，不走双层并存 crossfade */}
          <SpinePlayer
            key={active.id}
            asset={live2dAsset}
            fallback={active.fallback}
            title={active.name}
            className="gk-hero-live2d-player"
            eager
            playing={playing}
          />
        </div>
      </div>

      {playing && heroData && (
        <aside
          key={`info-${heroData.id}`}
          className="gk-hero-profile"
          aria-label={`${heroData.name} 角色信息`}
        >
          {heroData.kicker ? (
            <p className="gk-hero-profile-kicker" style={{ ['--i' as string]: 0 }}>
              {heroData.kicker}
            </p>
          ) : null}
          <h2 className="gk-hero-profile-name" style={{ ['--i' as string]: 1 }}>
            {heroData.name}
          </h2>
          {heroData.latin ? (
            <p className="gk-hero-profile-latin" style={{ ['--i' as string]: 2 }}>
              {heroData.latin}
            </p>
          ) : null}
          {quote ? (
            <p className="gk-hero-profile-quote" style={{ ['--i' as string]: 3 }}>
              “{quote}”
            </p>
          ) : null}
          <div className="gk-hero-profile-meta" style={{ ['--i' as string]: 4 }}>
            {heroData.stars > 0 && (
              <span className="gk-hero-profile-stars" aria-label={`${heroData.stars}星`}>
                {'★'.repeat(heroData.stars)}
              </span>
            )}
            <ul className="gk-hero-profile-traits">
              {heroData.traits.map((trait) => (
                <li key={`${trait.label}-${trait.name}`}>
                  <img src={trait.icon} alt="" />
                  <span>{trait.name}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      )}

      <button
        type="button"
        className="gk-hero-role-switch"
        title={`切换角色：${active.name}`}
        aria-label={`切换角色：${active.name}`}
        aria-haspopup="dialog"
        aria-expanded={selectorOpen}
        onClick={() => setSelectorOpen(true)}
      >
        <img className="gk-hero-role-switch-avatar" src={head} alt="" />
        <span className="gk-hero-role-switch-refresh" aria-hidden="true">
          <RefreshCw size={16} />
        </span>
      </button>

      {playing && (
        <a
          className="gk-hero-archive-btn"
          href={`/${gameId}/t${active.id}`}
          title={`查看 ${active.name} 图鉴`}
        >
          <span>进入图鉴</span>
          <ArrowUpRight size={16} strokeWidth={2.2} aria-hidden="true" />
        </a>
      )}

      {portalReady &&
        selectorOpen &&
        createPortal(
          <div
            className="gk-hero-role-selector-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setSelectorOpen(false);
            }}
          >
            <section
              className="gk-hero-role-selector"
              role="dialog"
              aria-modal="true"
              aria-labelledby="gk-hero-role-selector-title"
            >
              <header>
                <div>
                  <p>HERO DIRECTORY</p>
                  <h2 id="gk-hero-role-selector-title">切换角色</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectorOpen(false)}
                  aria-label="关闭"
                >
                  <X size={18} />
                </button>
              </header>
              <div className="gk-hero-role-selector-tools">
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="搜索英雄名称或编号"
                  aria-label="搜索英雄"
                />
                <span role="status">
                  {filtered.length} / {candidates.length}
                </span>
              </div>
              <div className="gk-hero-role-selector-grid">
                {filtered.map((hero) => (
                  <button
                    key={`${hero.gameId}:${hero.id}`}
                    type="button"
                    className={hero.id === active.id ? 'is-active' : undefined}
                    onClick={() => {
                      setActiveId(hero.id);
                      setSelectorOpen(false);
                      setSearch('');
                    }}
                  >
                    <img src={hero.head} alt="" loading="lazy" />
                    <b>{hero.name}</b>
                    {hero.id === active.id && <em>当前</em>}
                  </button>
                ))}
              </div>
              {!filtered.length && (
                <p className="gk-hero-role-selector-empty">没有找到符合条件的英雄</p>
              )}
            </section>
          </div>,
          document.body,
        )}
    </>
  );
}
